/**
 * This module is used for fetching and storing uploaded images from TimedRight's API.
 */
import { getAuthHeadersFromStorage } from '@front-app/services/api';
import { getImageFetchers } from '@front-app/workers/ImageWorkerWrapper';
import { reactive } from '@vue/composition-api';
import { ImageParams, ImageScale } from '@front-app/services/ImageApi/ImageModel';
import { STATUS } from '@front-app/constants/state';
import { ImageUrl } from '@front-app/services/ImageApi/ImageApi';
import { GetImage } from '@front-app/util/image';
import { TRImage } from '../util/image';

export interface AsyncImage {
	src: ImageUrl;
	status: STATUS;
}

export const LOADING_IMAGE: AsyncImage = {
	src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' as ImageUrl,
	status: STATUS.LOADING,
};

const ERROR_IMAGE: AsyncImage = {
	src: '' as ImageUrl,
	status: STATUS.ERROR,
};

export interface ImageStoreV2 {
	fetch(id: string, scale?: ImageScale, force?: boolean): Promise<void>;
	get(id: string, scale?: ImageScale): AsyncImage;
	invalidate(id: string, scale?: ImageScale, force?: boolean): Promise<void>;
}

type ImageMapV2 = Partial<Record<ImageScale, AsyncImage>>;
type ImageState = Record<string, ImageMapV2>;

export function createV2ImageStore(api: () => GetImage, defaultScale: ImageScale): ImageStoreV2 {
	const state = reactive({
		images: {} as ImageState,
	});
	const setImage = (id: string, scale: ImageScale, image: AsyncImage) => {
		const newState = { ...state.images };
		if (!state.images[id]) {
			newState[id] = getBaseImageMapV2();
		}
		newState[id][scale] = image;
		state.images = newState;
	};
	const get = (id: string, scale = defaultScale): AsyncImage => {
		const imageMapV2 = state.images[id];
		if (!imageMapV2) {
			return ERROR_IMAGE;
		}
		const imageAtRequestedSize = imageMapV2[scale];
		// If the image is done, return it, otherwise, try to find another finished version while it loads
		if (imageAtRequestedSize && imageAtRequestedSize.status === STATUS.SUCCESS) {
			return imageAtRequestedSize;
		}
		const otherSuccess = Object.values(imageMapV2)
			.find((_) => _?.status === STATUS.SUCCESS);
		return otherSuccess || imageAtRequestedSize || ERROR_IMAGE;
	};
	const fetch = async (id: string, scale = defaultScale, force = false): Promise<void> => {
		if (state.images[id]?.[scale] && !force) {
			return;
		}
		setImage(id, scale, LOADING_IMAGE);
		try {
			const image = await api()(id, getAuthHeadersFromStorage(), { scale });
			setImage(id, scale, {
				src: image,
				status: STATUS.SUCCESS,
			});
		} catch (e) {
			setImage(id, scale, ERROR_IMAGE);
		}
	};
	const invalidate = async (id: string, scale = defaultScale): Promise<void> => {
		if (!state.images[id]) {
			return;
		}
		const newState = { ...state.images };
		delete newState[id];
		state.images = newState;
		fetch(id, scale);
	};

	return {
		fetch,
		get,
		invalidate,
	};
}

function getBaseImageMapV2(): ImageMapV2 {
	return {};
}

type ImageMapV1 = Record<string, AsyncImage>;

export interface ImageStoreV1 {
	fetch(params: TRImage, overrideParams?: Partial<ImageParams>): Promise<void>;
	get(params: TRImage, overrideParams?: Partial<ImageParams>): AsyncImage;
}

export function createV1ImageStore(): ImageStoreV1 {
	const state = reactive({
		images: {} as ImageMapV1,
	});

	const get = (img: TRImage, overrideParams?: Partial<ImageParams>): AsyncImage => {
		if (img.src) {
			return {
				src: img.src as ImageUrl,
				status: STATUS.SUCCESS,
			};
		}
		if (img.params) {
			const paramHash = JSON.stringify({ ...img.params, ...overrideParams });
			return state.images[paramHash] || ERROR_IMAGE;
		}
		return ERROR_IMAGE;
	};

	const fetch = async (img: TRImage, overrideParams?: Partial<ImageParams>): Promise<void> => {
		if (img.src || !img.params) {
			return;
		}
		const mergedParams = { ...img.params, ...overrideParams };
		const mergedParamHash = JSON.stringify(mergedParams);
		if (state.images[mergedParamHash]) {
			return;
		}
		state.images = {
			...state.images,
			[mergedParamHash]: LOADING_IMAGE,
		};
		try {
			const image = await getImageFetchers().fetchV1Image(mergedParams, getAuthHeadersFromStorage());
			state.images = {
				...state.images,
				[mergedParamHash]: {
					src: image,
					status: STATUS.SUCCESS,
				},
			};
		} catch (e) {
			state.images = {
				...state.images,
				[mergedParamHash]: ERROR_IMAGE,
			};
		}
	};
	return {
		get,
		fetch,
	};
}
